
"use client";

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { SectionWrapper } from '../section-wrapper';
import { cn } from '@/lib/utils';
import { AnimatedSection } from '../animated-section';
import Image from 'next/image';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card } from '../ui/card';
import { Users, Heart, Stethoscope, ChefHat, Building, School, Landmark, Mic } from 'lucide-react';

const audiences = [
  { text: "Personas con celiaquía y sensibilidad al gluten", icon: Heart, className: "p-4 w-52 h-52", mobileClassName: "p-6" },
  { text: "Familiares y entorno", icon: Users, className: "p-4 w-40 h-40", mobileClassName: "p-5" },
  { text: "Profesionales sanitarios", icon: Stethoscope, className: "p-4 w-48 h-48", mobileClassName: "p-6" },
  { text: "Hostelería y restauración", icon: ChefHat, className: "p-4 w-44 h-44", mobileClassName: "p-5" },
  { text: "Asociaciones de pacientes", icon: Building, className: "p-4 w-44 h-44", mobileClassName: "p-5" },
  { text: "Entorno educativo", icon: School, className: "p-4 w-36 h-36", mobileClassName: "p-4" },
  { text: "Administración pública", icon: Landmark, className: "p-4 w-48 h-48", mobileClassName: "p-6" },
  { text: "Medios de comunicación", icon: Mic, className: "p-3 w-40 h-40", mobileClassName: "p-5" },
];


interface Point {
    x: number;
    y: number;
}

const CentralCircle = ({ elementRef }: { elementRef: React.RefObject<HTMLDivElement | null> }) => (
    <div ref={elementRef} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 group/center transition-all duration-500">
        <div className="relative z-10 bg-card rounded-full aspect-square flex flex-col items-center justify-center text-center p-4 shadow-2xl border-4 border-primary w-44 h-44 sm:w-60 sm:h-60 md:w-72 md:h-72 transition-transform duration-300 group-hover/center:scale-105">
            <Image src="/img/logo.png" alt="Sin Gluten Experience Day Logo" width={300} height={88} className="object-contain" />

        </div>
    </div>
);

const ConnectingLine = ({ from, to }: { from: Point | null, to: Point | null }) => {
    if (!from || !to) return null;

    return (
        <svg
            className="absolute top-0 left-0 w-full h-full z-0 transition-opacity duration-1000 ease-out opacity-100"
            style={{ transitionDelay: '500ms' }}
        >
            <line
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                strokeDasharray="5, 5"
            />
        </svg>
    );
};

const AudienceCircles = () => {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const centralCircleRef = useRef<HTMLDivElement>(null);
    const linePointsRef = useRef<{ from: Point; to: Point; }[]>([]);
    
    // State to force re-render on resize/zoom
    const [version, setVersion] = useState(0);

    const satelliteRefs = React.useMemo(() =>
        Array(audiences.length).fill(0).map(() => React.createRef<HTMLDivElement>()),
        []
    );
    
    const calculatePositions = useCallback(() => {
        if (!isVisible || !containerRef.current) return;
        
        const centralElem = centralCircleRef.current;
        const parentRect = containerRef.current?.getBoundingClientRect();

        if (!centralElem || !parentRect) return;

        const centralRect = centralElem.getBoundingClientRect();
        const centralPoint = {
            x: centralRect.left + centralRect.width / 2 - parentRect.left,
            y: centralRect.top + centralRect.height / 2 - parentRect.top,
        };
        const centralRadius = centralRect.width / 2;
        
        const newPoints = satelliteRefs.map(ref => {
            const elem = ref.current;
            if (!elem) return null;
            
            const rect = elem.getBoundingClientRect();
            const satellitePoint = {
                x: rect.left + rect.width / 2 - parentRect.left,
                y: rect.top + rect.height / 2 - parentRect.top,
            };
            
            const dx = satellitePoint.x - centralPoint.x;
            const dy = satellitePoint.y - centralPoint.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance === 0) return null;
            
            const from = {
                 x: centralPoint.x + (dx * centralRadius) / distance,
                 y: centralPoint.y + (dy * centralRadius) / distance,
            };

            const satelliteRadius = rect.width / 2;
            const newDistance = distance - satelliteRadius;

            const to = {
                x: centralPoint.x + (dx * newDistance) / distance,
                y: centralPoint.y + (dy * newDistance) / distance,
            };
            
            return { from, to };

        }).filter(p => p !== null) as { from: Point; to: Point; }[];
        
        linePointsRef.current = newPoints;
    }, [isVisible, satelliteRefs]);
        
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.2 }
        );

        const currentRef = sectionRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, []);

    useLayoutEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver(() => {
            // Force a re-render which will trigger position calculation
            setVersion(v => v + 1);
        });
        
        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Calculate positions on every render
    calculatePositions();

    const getCirclePosition = useCallback((index: number, total: number) => {
        const containerWidth = containerRef.current?.clientWidth || 1000;
        const baseRadius = containerWidth * 0.35;

        const random = (seed: number) => {
            var x = Math.sin(seed) * 10000;
            return x - Math.floor(x);
        }

        const angleOffset = random(index + 1) * 20 - 10;
        const baseAngle = (360 / total) * index;
        const angle = (baseAngle + angleOffset) * (Math.PI / 180);

        const radiusVariation = random(index + 2) * (baseRadius * 0.15) - (baseRadius * 0.075);
        const radius = baseRadius + radiusVariation;
        
        const landscapeFactor = Math.abs(Math.cos(angle)); 
        const finalRadius = radius * (1 + landscapeFactor * 0.1);

        let x = finalRadius * Math.cos(angle);
        let y = finalRadius * Math.sin(angle) * 0.85;

        // Specific adjustment for "Profesionales sanitarios" (index 2)
        if (index === 2) {
            x -= 50; 
            y -= 20;
        }

        return {
            transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
        };
    }, []);

    return (
        <div ref={sectionRef} className="mt-12 md:mt-20">
            <div ref={containerRef} className="relative mx-auto w-full max-w-6xl h-[50rem] sm:h-[60rem]">
                 {isVisible && linePointsRef.current.map((points, index) => (
                    <ConnectingLine key={index} from={points.from} to={points.to} />
                ))}
                <CentralCircle elementRef={centralCircleRef} />
                
                {audiences.map((audience, index) => {
                    const positionStyle = getCirclePosition(index, audiences.length);

                    return (
                        <div
                            key={index}
                            ref={satelliteRefs[index]}
                            className={cn(
                                "absolute top-1/2 left-1/2 transition-all duration-700 ease-out",
                                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                            )}
                            style={{
                                ...positionStyle,
                                transitionDelay: `${100 * index}ms`
                            }}
                        >
                            <div className={cn("relative z-10 bg-primary/10 border border-primary/20 text-foreground rounded-full aspect-square flex items-center justify-center text-center shadow-lg transition-transform duration-300 hover:scale-105", audience.className)}>
                                <p className="text-sm sm:text-base font-semibold">{audience.text}</p>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    );
};

const MobileAudienceList = () => {
  return (
    <div className="mt-8 space-y-4">
      <div className="flex justify-center my-8">
         <Image src="/img/logo.png" alt="Sin Gluten Experience Day Logo" width={300} height={88} className="object-contain" style={{ width: 'auto', height: 'auto' }} />
      </div>
      {audiences.map((audience, index) => {
        const Icon = audience.icon;
        return (
            <AnimatedSection key={index} delay={index * 100}>
                <Card className="bg-primary/10 p-4">
                    <div className="flex items-center gap-4">
                        <div className="bg-primary/20 p-3 rounded-full">
                            <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <p className="font-semibold text-foreground flex-1">{audience.text}</p>
                    </div>
                </Card>
            </AnimatedSection>
        )
      })}
    </div>
  )
}

export const TargetAudienceSection = () => {
    const isMobile = useIsMobile();
    
    return (
        <SectionWrapper id="target-audience" className="bg-background text-foreground overflow-hidden py-12 md:py-16 lg:py-20">
            <AnimatedSection>
                <div className="mx-auto max-w-4xl text-center mb-4 md:mb-8">
                    <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl font-headline">
                        ¿A QUIÉN VA <span className="text-primary">DIRIGIDO?</span>
                    </h2>
                </div>
            </AnimatedSection>
            {isMobile ? <MobileAudienceList /> : <AudienceCircles />}
        </SectionWrapper>
    );
};

    